<script setup>
import { ref, watch, onUnmounted } from 'vue';

// 1. Assign Props
const props = defineProps({
  userLocation: {
    type: String,
    default: 'London'
  }
});

// 2. Reactive State
const locationName = ref('');
const formattedTime = ref('');
const formattedDate = ref('');
const timeZoneName = ref('');
const loading = ref(false);
const errorMessage = ref('');

// Interval handle for the live ticking clock
let clockInterval = null;
let currentTimeZone = null;

// 3. Clock Ticking Function
const updateClock = () => {
  if (!currentTimeZone) return;

  const now = new Date();

  // Format Time (HH:MM:SS) in local timezone
  formattedTime.value = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentTimeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(now);

  // Format Date (e.g., Monday, 27 July 2026)
  formattedDate.value = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentTimeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(now);
};

// Start or restart the 1-second interval ticker
const startClock = (timeZone) => {
  currentTimeZone = timeZone;
  if (clockInterval) clearInterval(clockInterval);
  updateClock();
  clockInterval = setInterval(updateClock, 1000);
};

// 4. Fetch Timezone Data
const fetchTimeZone = async () => {
  const input = props.userLocation ? props.userLocation.trim() : '';
  if (!input) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    let lat, lon, displayName;
    const isPostcode = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i.test(input);

    if (isPostcode) {
      const pcRes = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(input)}`);
      const pcData = await pcRes.json();
      if (pcData.status !== 200) throw new Error('Invalid UK Postcode');
      lat = pcData.result.latitude;
      lon = pcData.result.longitude;
      displayName = `${pcData.result.outward_code}, ${pcData.result.admin_district}`;
    } else {
      const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(input)}&count=5&language=en&format=json`);
      const geoData = await geoRes.json();
      if (!geoData.results || geoData.results.length === 0) throw new Error('Town or location not found');

      const ukMatch = geoData.results.find(place => place.country_code === 'GB') || geoData.results[0];
      lat = ukMatch.latitude;
      lon = ukMatch.longitude;
      displayName = `${ukMatch.name}${ukMatch.admin1 ? ', ' + ukMatch.admin1 : ''}`;
    }

    // Lookup precise Timezone string (e.g., Europe/London) via Open-Meteo
    const tzRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m&timezone=auto`);
    if (!tzRes.ok) throw new Error('Failed to fetch timezone');
    
    const tzData = await tzRes.json();
    if (!tzData.timezone) throw new Error('Timezone data unavailable');

    locationName.value = displayName;
    timeZoneName.value = tzData.timezone;

    // Start live clock with retrieved timezone
    startClock(tzData.timezone);

  } catch (err) {
    if (clockInterval) clearInterval(clockInterval);
    errorMessage.value = err.message || 'Error fetching time data';
  } finally {
    loading.value = false;
  }
};

// 5. Watchers & Cleanups
watch(
  () => props.userLocation,
  (newVal) => {
    if (newVal) fetchTimeZone();
  },
  { immediate: true }
);

// Clean up timer when component unmounts to prevent memory leaks
onUnmounted(() => {
  if (clockInterval) clearInterval(clockInterval);
});
</script>

<template>
  <div class="time-card">
    <!-- Loading State -->
    <div v-if="loading" class="state-message">
      Loading time for {{ userLocation }}...
    </div>

    <!-- Error State -->
    <div v-else-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- Time Display -->
    <div v-else-if="formattedTime" class="time-info">
      <!-- <h2 class="location">{{ locationName }}</h2> -->
      
      <div class="clock-display">
        <i class="bi bi-clock-fill clock-icon"></i>
        <div class="digital-time">{{ formattedTime }}</div>
      </div>

      <div class="date-display">{{ formattedDate }}</div>
      <div class="tz-badge">{{ timeZoneName }}</div>
    </div>
  </div>
</template>

<style scoped>
/* Time Card Wrapper - Fixed 370px Height + Centered Content */
.time-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
  width: 100%;
  
  height: 200px;
  box-sizing: border-box;
  
  /* Flexbox settings for vertical & horizontal centering */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  margin: 0 auto;
}

/* Ensure inner content wrapper spans full width */
.time-info {
  width: 100%;
}

/* Status Messages */
.state-message {
  color: #64748b;
  font-size: 0.95rem;
}

.error-message {
  color: #e11d48;
  font-size: 0.95rem;
}

/* Header & Location */
.location {
  font-size: 1.25rem;
  color: #334155;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

/* Clock Display */
.clock-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 1rem 0 0.5rem 0;
}

.clock-icon {
  font-size: 2.25rem;
  color: #0284c7;
}

.digital-time {
  font-size: 2.75rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums; /* Prevents text jitter */
}

/* Date & Timezone */
.date-display {
  font-size: 0.95rem;
  font-weight: 500;
  color: #475569;
  margin-top: 0.25rem;
}

.tz-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 4px 10px;
  border-radius: 20px;
  margin-top: 1rem;
}
</style>